import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import useStore from '../../../../store';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaBackspace, FaPaperPlane, FaQuestionCircle } from 'react-icons/fa';
import UnifiedLeaderboard from '../../ui/UnifiedLeaderboard';
import './Slovodel.css';
import '../../ui/ArcadeShared.css';

// Определяем наличие тач-интерфейса
const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

const Slovodel = ({ socket, onClose, timeLeft }) => {
  const { t } = useTranslation();
  const store = useStore.getState();

  const [state, setState] = useState(null);

  // КЛЮЧЕВОЕ ИЗМЕНЕНИЕ: храним индексы выбранных плиток в порядке их выбора
  const [usedIndices, setUsedIndices] = useState([]);

  const [activeDef, setActiveDef] = useState(null);
  const autoShowEnabledRef = useRef(true);
  const hasShownBaseWordRef = useRef(false);

  const [submitStatus, setSubmitStatus] = useState('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [showStamp, setShowStamp] = useState(false);
  const [finalData, setFinalData] = useState(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isShaking, setIsShaking] = useState(false);

  const inputRef = useRef(null);

  // Вычисляемое значение строки для инпута и отправки
  const inputVal = useMemo(() => {
    if (!state?.baseWord) return '';
    return usedIndices.map(index => state.baseWord[index]).join('');
  }, [usedIndices, state?.baseWord]);

  // Унифицированная функция открытия определения
  const openDefinition = useCallback((word, definitionsMap) => {
    const key = word.toLowerCase();
    const text = definitionsMap?.[key];
    if (text) {
      setActiveDef({ word: word.toUpperCase(), text });
      // ЕСЛИ ПОЛЬЗОВАТЕЛЬ ОТКРЫЛ ВРУЧНУЮ — ВКЛЮЧАЕМ АВТО-РЕЖИМ
      autoShowEnabledRef.current = true;
    }
  }, []);

  // Нормализация для сравнения (е/ё)
  const normalizeChar = (c) => c.toLowerCase().replace(/ё/g, 'е');

  // Функция тряски
  const triggerError = useCallback(() => {
    setIsShaking(true);
    window.croco?.haptic?.('error');
    setTimeout(() => setIsShaking(false), 400);
  }, []);

  /**
   * Умное добавление буквы по её символу (поиск свободной плитки)
   */
  const addCharBySymbol = useCallback((symbol) => {
    if (!state?.baseWord) return;
    const charToFind = normalizeChar(symbol);

    const nextFreeIndex = state.baseWord.split('').findIndex((c, idx) =>
      normalizeChar(c) === charToFind && !usedIndices.includes(idx)
    );

    if (nextFreeIndex !== -1) {
      setUsedIndices(prev => [...prev, nextFreeIndex]);
      // УБРАНО: window.croco?.haptic?.('selection');
    } else {
      triggerError();
    }
  }, [state?.baseWord, usedIndices, triggerError]);

  /**
   * Управление фокусом:
   * shouldBlur = true при нажатии на плитки-буквы (скрыть клаву).
   * shouldBlur = false для возврата фокуса на ПК или сохранения на таче.
   */
  const manageFocusAfterAction = useCallback((shouldBlur = false) => {
    if (!inputRef.current || finalData) return;
    if (isTouchDevice) {
      if (shouldBlur) inputRef.current.blur();
    } else {
      inputRef.current.focus();
    }
  }, [finalData]);

  const showFeedback = useCallback((status, message, definition, word) => {
    setSubmitStatus(status);
    setStatusMessage(message);
    setShowStamp(true);

    if (status === 'success') {
      // АВТО-ПОКАЗ РАБОТАЕТ ТОЛЬКО ЕСЛИ ФЛАГ autoShowEnabled ВЕРЕН
      if (definition && autoShowEnabledRef.current) {
        setActiveDef({ word: word.toUpperCase(), text: definition });
      } else {
        // Если определения нет ИЛИ авто-показ выключен — скрываем текущее окно (чтобы не висел старый текст)
        setActiveDef(null);
      }
    }

    setTimeout(() => {
      setSubmitStatus('idle');
      setShowStamp(false);
    }, 1200);
  }, []);

  // Обработчик клика по слову в списке
  const handleWordClick = (word) => {
    openDefinition(word, state.definitionsMap);
    window.croco?.haptic?.('selection');
  };

  useEffect(() => {
    if (!socket) return;
    store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'init' });


    const handleEvent = (data) => {
      if (data.type === 'update') {
        setState(data.state);

        // ПОКАЗЫВАЕМ БАЗОВОЕ СЛОВО ТОЛЬКО ПРИ ПЕРВОМ ВХОДЕ
        if (!hasShownBaseWordRef.current && data.state.baseWord) {
          const baseKey = data.state.baseWord.toLowerCase();
          const baseDef = data.state.definitionsMap?.[baseKey];
          if (baseDef && autoShowEnabledRef.current) {
            setActiveDef({ word: data.state.baseWord.toUpperCase(), text: baseDef });
            hasShownBaseWordRef.current = true; // Устанавливаем флаг после первого показа
          }
        }
      }
      else if (data.type === 'success') {
        setState(data.state);
        // ПЕРЕДАЕМ ТЕКУЩЕЕ СОСТОЯНИЕ ФЛАГА В FEEDBACK
        showFeedback('success', `+${data.points} ${data.word.toUpperCase()}`, data.definition, data.word);
        window.croco?.haptic?.('success');
        setUsedIndices([]);
      }
      else if (data.type === 'reject') {
        const msg = t(`arcade.slovodel_err_${data.reason}`, data.reason.toUpperCase());
        // Для повторяющихся слов используем warning (синий) вместо error (красный)
        const status = (data.reason === 'repeat_self' || data.reason === 'repeat_global') ? 'warning' : 'error';
        showFeedback(status, msg, null, null);
        window.croco?.haptic?.('warning');
        setUsedIndices([]);
      }
      else if (data.type === 'finish' || data.type === 'game_over') {
        setFinalData(data);
      }
    };

    socket.on('arcade:event', handleEvent);
    return () => socket.off('arcade:event', handleEvent);
    // Добавляем autoShowEnabled в зависимости, чтобы сокет видел актуальный флаг
  }, [socket, t, store, showFeedback]);

  // Эффект для поддержания фокуса на ПК при изменении состояния
  useEffect(() => {
    if (!isTouchDevice && state && !finalData) {
      inputRef.current?.focus();
    }
  }, [state, finalData]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!inputVal.trim() || isRequesting) return;

    setIsRequesting(true);
    store.ref.work.methods.hostAction('mafiaMiniGameAction', {
        type: 'submit',
        word: inputVal.toLowerCase()
    });

    setUsedIndices([]);
    setTimeout(() => setIsRequesting(false), 100);

    // После отправки: на ПК возвращаем фокус, на таче — оставляем как есть
    manageFocusAfterAction(false);
  };

  /**
   * Логика нажатия на кнопку буквы
   */
  const handleLetterClick = (clickedIndex, char) => {
    if (isRequesting || submitStatus !== 'idle') return;
    const lastIndex = usedIndices[usedIndices.length - 1];

    if (clickedIndex === lastIndex) {
      setUsedIndices(prev => prev.slice(0, -1));
      window.croco?.haptic?.('impactLight');
    } else if (!usedIndices.includes(clickedIndex)) {
      setUsedIndices(prev => [...prev, clickedIndex]);
      // УБРАНО: window.croco?.haptic?.('selection');
    } else {
      addCharBySymbol(char);
    }

    // ИСПОЛЬЗУЕМ ФУНКЦИЮ: на таче это вызовет blur(), клавиатура не вылезет
    manageFocusAfterAction(true);
  };

  /**
   * Логика ввода с клавиатуры
   */
  /**
   * ИСПРАВЛЕНИЕ ДЛЯ ANDROID: Обработка изменения инпута
   */
  const handleInput = (e) => {
    const newValue = e.target.value;
    if (newValue.length < inputVal.length) {
      setUsedIndices(prev => prev.slice(0, -1));
    } else {
      const addedChar = newValue.slice(-1);
      addCharBySymbol(addedChar);
    }
    // После ввода с клавиатуры на ПК — возвращаем фокус
    manageFocusAfterAction(false);
  };

  /**
   * Специальный обработчик для системных кнопок (Backspace, Отправить, Очистить)
   * Используем preventDefault, чтобы инпут не терял фокус при нажатии
   */
  const handleSystemBtnPointerDown = (e) => {
    // На таче, если инпут в фокусе, не даем кнопке перехватить его
    if (isTouchDevice && document.activeElement === inputRef.current) {
      e.preventDefault();
    }
  };

  const handleClearAll = (e) => {
    setUsedIndices([]);
    window.croco?.haptic?.('impactLight');
    // Фокус не теряется благодаря preventDefault в onPointerDown
  };



  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };


  if (finalData) {
      // Данные игрока могут быть в data.my или в data.finalData.my в зависимости от типа пакета
      const myStats = finalData.my || finalData.finalData?.my || {};
      const { score, wordsCount, rank } = myStats;

      return (
          <div className="slovodel-board">
              <div className="sd-gameover add-scroll touch-active">
                  <div className="go-title">{t('arcade.slovodel_finished', 'ДЕЛО ЗАКРЫТО')}</div>
                  <div className="go-score">{score}⭐️</div>
                  <div style={{color:'#fff', marginBottom:'5px'}}>{t('arcade.slovodel_words_found')}: {wordsCount}</div>
                  <div className="go-rank">{t(`arcade.slovodel_rank_${rank}`, rank)}</div>

                  {/* Теперь данные точно будут здесь */}
                  <UnifiedLeaderboard
                      results={finalData.leaderboard}
                      myId={store.ref.telegram.telegramUserId}
                      t={t}
                  />

                  <button className="go-btn" onClick={onClose}>{t('arcade.close', 'В АРХИВ')}</button>
              </div>
          </div>
      );
  }

  if (!state) return null;

  return (
    <div
        className="slovodel-board"
        onPointerDown={() => {
            // На ПК клик в пустоту возвращает фокус. На таче — НЕТ (чтобы не прыгала клава)
            if (!isTouchDevice) manageFocusAfterAction(false);
        }}
    >
        <div className="sd-header">
            <div className="sd-title" onClick={() => handleWordClick(state.baseWord)}>
                {state.baseWord.toUpperCase()}
            </div>
            <div className="sd-timer">
                {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
            </div>
            <div className="sd-score">{state.score}⭐️</div>
        </div>

        {/* Теперь под хедером только инпут и кнопки букв */}
        <div className="sd-input-area">
            <div className="sd-input-wrapper">
                <input
                    ref={inputRef}
                    className="sd-input"
                    value={inputVal.toUpperCase()}
                    onInput={handleInput} // Используем onInput для поддержки Android
                    onKeyDown={handleKeyDown}
                    placeholder={t('arcade.slovodel_placeholder_letters', {letters: state.minWordLength})}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                    inputMode="text" // Подсказываем мобилкам тип клавиатуры
                />
                {inputVal && (
                    <div
                        className="sd-input-clear-x"
                        onPointerDown={handleSystemBtnPointerDown}
                        onClick={handleClearAll}
                    >
                        <FaTimes />
                    </div>
                )}
            </div>
            <button
                type="button"
                className="sd-action-btn-square"
                onPointerDown={handleSystemBtnPointerDown}
                onClick={() => { setUsedIndices(prev => prev.slice(0, -1)); window.croco?.haptic?.('impactLight'); }}
            >
                <FaBackspace />
            </button>
        </div>

        <div className={`sd-base-word-container ${isShaking ? 'shake-kb' : ''}`}>
            {state.baseWord.split('').map((char, i) => {
                const isUsed = usedIndices.includes(i);
                const isLast = usedIndices[usedIndices.length - 1] === i;

                return (
                    <div
                      key={i}
                      className={`sd-letter-tile ${isUsed ? 'used' : ''} ${isLast ? 'last-char' : ''}`}
                      onPointerDown={handleSystemBtnPointerDown} // Важно: не даем плиткам забирать фокус
                      onClick={() => handleLetterClick(i, char)}
                    >
                        {char.toUpperCase()}
                    </div>
                );
            })}
        </div>

        <button
          className={`sd-submit-btn-large status-${submitStatus}`}
          onPointerDown={handleSystemBtnPointerDown}
          onClick={handleSubmit}
          disabled={!inputVal.trim() || isRequesting}
        >
            {submitStatus === 'success' ? (
                <>{statusMessage}</> // Покажет "+5 СЛОВО"
            ) : submitStatus === 'error' || submitStatus === 'warning' ? (
                statusMessage
            ) : (
                <><FaPaperPlane style={{marginRight: '10px'}}/> {t('arcade.slovodel_send')}</>
            )}
        </button>

        {/* ЗОНА СПИСКА И ОПРЕДЕЛЕНИЙ */}
        <div className="sd-list-container add-scroll">

            {/* Определение показывается НАД списком, если оно активно */}
            {activeDef && (
              <div
                className="sd-definition-panel"
                onClick={() => {
                  setActiveDef(null);
                  autoShowEnabledRef.current = false;
                }}
              >
                <span className="sd-def-word">{activeDef.word}</span>
                <div className="sd-def-text">{activeDef.text}</div>
                {/* Вспомогательная надпись удалена */}
              </div>
            )}

            {/* Хинт slovodel_start_hint показывается ВСЕГДА, когда список пуст */}
            {state.foundWords.length === 0 ? (
              <div className="sd-empty-hint">{t('arcade.slovodel_start_hint')}</div>
            ) : (
              <div className="sd-words-cloud">
                {state.foundWords.map((word, i) => {
                  const hasDef = !!state.definitionsMap?.[word.toLowerCase()];
                  return (
                    <div
                      key={i}
                      className={`sd-word-tag ${hasDef ? 'has-def' : ''}`}
                      onClick={() => handleWordClick(word)}
                    >
                      {word.toUpperCase()}
                      {hasDef && <FaQuestionCircle className="sd-word-help-icon" />}
                    </div>
                  );
                })}
              </div>
            )}
        </div>

        {/* 6. Stamp Overlay */}
        {showStamp && (
          <div className="sd-stamp-overlay">
             <div className={`sd-stamp ${submitStatus === 'success' ? 'stamp-ok' : submitStatus === 'warning' ? 'stamp-warning' : 'stamp-fail'}`}>
                {statusMessage}
             </div>
          </div>
        )}
    </div>
  );
};

export default Slovodel;